/**
 * Enregistrement manuel du service worker.
 *
 * On n'utilise pas `virtual:pwa-register` pour eviter la dependance
 * workbox-window : le cycle de vie dont on a besoin tient en quelques lignes.
 */

const SW_URL = import.meta.env.DEV ? '/dev-sw.js?dev-sw' : '/sw.js';
const SW_TYPE: WorkerType = import.meta.env.DEV ? 'module' : 'classic';

export interface SwHandle {
  /** Appele quand une nouvelle version attend d'etre activee. */
  onUpdateAvailable?: () => void;
}

export async function registerServiceWorker(handle: SwHandle = {}): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, {
      type: SW_TYPE,
      scope: '/',
    });

    if (registration.waiting) handle.onUpdateAvailable?.();

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // `controller` absent = toute premiere installation, pas une mise a
        // jour : inutile de proposer un rechargement.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          handle.onUpdateAvailable?.();
        }
      });
    });

    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  } catch (error) {
    console.error('service worker non enregistre', error);
  }
}

/**
 * Repart de zero : desenregistre les workers et vide leurs caches, puis
 * recharge. C'est la sortie de secours d'une installation restee coincee —
 * l'enregistrement mort-ne n'est jamais retente tout seul, et rien d'autre ne
 * peut le debloquer depuis la page.
 *
 * Aucune donnee n'est perdue : tout vit sur le serveur, le cache ne contient
 * que le shell de l'application.
 */
export async function resetServiceWorker(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((item) => item.unregister().catch(() => undefined)));
  }
  if ('caches' in globalThis) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key).catch(() => undefined)));
  }
  globalThis.location.reload();
}

export async function applyUpdate(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration();
  registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
}
